import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import HttpClient from '../../services/HttpClient'
import './HanList.css'

/**
 * HanList — Lista compacta de Han (territorios internos) por imperio
 * - Grid de chips muy compactos
 * - Búsqueda local
 * - Carga incremental de 24 ítems con botón "Ver más"
 * - Badge con conteo de aventuras (adventureCount) si viene del backend
 */
export default function HanList({ parentSlug }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [visibleCount, setVisibleCount] = useState(24)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!parentSlug) return
    setLoading(true)
    setError(null)
    setItems([])
    setVisibleCount(24)

    HttpClient()
      .get(`/api/v1/territory/internal/${parentSlug}`)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : []
        setItems(list)
      })
      .catch((e) => {
        console.error('[HanList] Error fetching Han:', e)
        setError('No se pudieron cargar los Han')
        setItems([])
      })
      .finally(() => setLoading(false))
  }, [parentSlug])

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase()
    let list = items || []
    if (q) {
      list = list.filter((t) => {
        const name = String(t?.name || '').toLowerCase()
        const desc = String(t?.description || '').toLowerCase()
        const slug = String(t?.slug || '').toLowerCase()
        return name.includes(q) || desc.includes(q) || slug.includes(q)
      })
    }
    // Orden alfabético por nombre para estabilidad
    return [...list].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
  }, [items, query])

  const visible = useMemo(() => filtered.slice(0, Math.max(0, visibleCount)), [filtered, visibleCount])

  const handleShowMore = () => {
    setVisibleCount((c) => c + 24)
  }

  const renderHeader = () => (
    <div className="hanlist-header">
      <h3 className="hanlist-title">Han del Shogunato</h3>
      <div className="hanlist-tools">
        <div className="hanlist-counter" aria-live="polite">
          {filtered.length} han{filtered.length !== 1 ? 's' : ''}
        </div>
        <div className="hanlist-search">
          <input
            type="search"
            className="hanlist-input"
            placeholder="Buscar han..."
            aria-label="Buscar han por nombre o descripción"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <section className="hanlist">
        {renderHeader()}
        <div className="hanlist-grid">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="han-chip skeleton" aria-hidden="true">
              <div className="han-chip-name skeleton-bar" />
              <div className="han-chip-meta">
                <span className="han-badge skeleton-dot" />
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="hanlist">
        {renderHeader()}
        <div className="hanlist-empty">
          <p role="alert">{error}</p>
        </div>
      </section>
    )
  }

  if (!filtered || filtered.length === 0) {
    return (
      <section className="hanlist">
        {renderHeader()}
        <div className="hanlist-empty">
          <h4>No hay Han registrados</h4>
          <p>Intenta cambiar el término de búsqueda.</p>
        </div>
      </section>
    )
  }

  return (
    <section className="hanlist">
      {renderHeader()}

      <div className="hanlist-grid">
        {visible.map((t) => {
          const adventureCount = Number(t?.adventureCount ?? 0)
          const hasCount = Number.isFinite(adventureCount) && adventureCount > 0
          const status = String(t?.territory_status || '').trim()
          const diff = String(t?.difficulty || '').trim()
          const ariaLabel = `Han ${t?.name || ''}${hasCount ? `, ${adventureCount} aventuras` : ''}${status ? `, estado ${status}` : ''
            }`

          return (
            <Link
              key={t.territoryId || t.slug || t.name}
              to={`/han/${t.slug}`}
              className="han-chip"
              aria-label={ariaLabel}
            >
              <div className="han-chip-row">
                <span className="han-chip-name">{t.name}</span>
                {hasCount ? <span className="han-badge" title="Aventuras">🎯 {adventureCount}</span> : null}
              </div>

              <div className="han-chip-meta">
                {status ? (
                  <span className={`han-status han-status-${status.toLowerCase().replace(/\s+/g, '-')}`}>{status}</span>
                ) : (
                  <span className="han-status han-status-na">—</span>
                )}
                {diff ? <span className={`han-diff han-diff-${diff.toLowerCase()}`}>{diff}</span> : null}
              </div>
            </Link>
          )
        })}
      </div>

      {visible.length < filtered.length ? (
        <div className="hanlist-actions">
          <button type="button" className="hanlist-more" onClick={handleShowMore} aria-label="Cargar más han">
            Ver más
          </button>
        </div>
      ) : null}
    </section>
  )
}